import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { toLower } from 'lodash-es';
import { map } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { DOCUMENT } from 'src/app/feature/injection-token/document';
import { WINDOW } from 'src/app/feature/injection-token/window';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { validString } from 'src/app/shared/utilities/types.utils';
import { getRouteParamsFromUrl } from 'src/app/shared/utilities/window.utils';
import { postApiRequest } from '../api/api.actions';
import { clearApplicationConfigAction } from '../application-config/application-config.actions';
import { initializeStart } from '../initialize/initialize.actions';
import { LocaleIdService } from '../locale/locale-id.service';
import { clearLocales, fetchLocaleFile } from '../locale/locale.actions';
import { LocaleService } from '../locale/locale.service';
import { fullRouteTo } from '../routing/routing.actions';
import { setLanguage, updateClientConfiguration } from './client-configuration.actions';
import { ClientConfigurationState } from './client-configuration.state';

/**
 * Handles client configuration. Things like merchant id, tenant, language.
 */
@Injectable()
export class ClientConfigurationEffects {
	/**
	 * Handles watching the initializeStart action to start our client configuration parsing.
	 */
	initializeClientConfiguration$ = createEffect(() =>
		this._actions$.pipe(
			ofType(initializeStart),
			map(() => updateClientConfiguration({ payload: this._getConfiguration() }))
		)
	);

	/**
	 * Handles request to update the client configuration. Things like setting the default
	 * language get handled here.
	 */
	updateClientConfiguration$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(updateClientConfiguration),
				map((action) => {
					if (action.payload.error) {
						return fullRouteTo({ payload: ['/invalid'] });
					}
					this._localeId.setLocale(action.payload.language);
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles request to set a specific language.
	 */
	setLanguage$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(setLanguage),
				map((action) => {
					const { payload } = action;

					if (this._localeId?.toValue()?.toLowerCase() === payload?.toLowerCase()) {
						return;
					}

					this._localeId.setLocale(payload);
					const sections = this._locale.getLocaleSections();

					[
						clearLocales(),
						fetchLocaleFile({ payload }),
						postApiRequest({
							requestType: ApiRequestType.GET_APPLICATION_LOCALE,
							params: {
								section_list: sections.join(','),
							},
							customData: {
								updateURL: true,
							},
						}),
						clearApplicationConfigAction(),
						postApiRequest({ requestType: ApiRequestType.GET_APPLICATION_CONFIG }),
					].forEach((newAction) => this._store$.dispatch(newAction));
				})
			),
		{ dispatch: false }
	);

	constructor(
		@Inject(WINDOW) private _window: Window,
		@Inject(DOCUMENT) private _document: Document,
		private _store$: Store<AppState>,
		private _actions$: Actions,
		private _localeId: LocaleIdService,
		private _locale: LocaleService
	) {}

	/**
	 * Returns the parent url
	 * @param url The referrer url to parse
	 */
	private _getParentHostname(url: string): string {
		if (validString(url)) {
			const el = this._document.createElement('a');
			el.href = url;
			return `${el.protocol}//${el.host}`;
		}

		return '';
	}

	/**
	 * Returns the initial configuration when initializing the application
	 */
	private _getConfiguration(): ClientConfigurationState {
		const routeParams = getRouteParamsFromUrl(this._window);
		const [tenantName, merchantId, language] = routeParams;
		const count = routeParams?.length;
		const config: ClientConfigurationState = {
			error: false,
			found: false,
			tenantName,
			language: this._localeToCLDR(language),
			merchantId,
			originUrl: '',
			parentHostname: this._getParentHostname(this._document.referrer),
		};

		if (count > 1) {
			if (count > 2) {
				config.error = false;
				config.found = true;
			} else {
				config.error = false;
				config.found = true;
				config.language = '';
			}
			return config;
		}
		config.error = true;
		config.found = false;
		config.tenantName = '';
		config.language = '';
		config.merchantId = '';

		return config;
	}

	/**
	 * Converts non-standard locales to CLDR locales
	 * @param locale The _locale to convert
	 */
	private _localeToCLDR(locale: string = ''): string {
		let languageLowercased = toLower(locale).toString();
		// if we have an underscore, replace it with a dash
		if (languageLowercased.includes('_')) {
			languageLowercased = languageLowercased.replace('_', '-');
		}

		// Fix items where language equals the country, such as fr-FR or de-DE. Exclusion is pt-pt where cldr does have this.
		const [language, country] = languageLowercased.split('-');
		if (validString(languageLowercased) && language === country && languageLowercased !== 'pt-pt') {
			return language;
		}

		const newLocale = this._parseLocale(languageLowercased);
		return this._transformLocale(newLocale);
	}

	/**
	 * Transforms the locale region/country to the correct format
	 * for angular common locale data
	 * @param locale string
	 * @returns localeToTransform string
	 */
	private _transformLocale(locale: string): string {
		const localesWithAntiPattern = [
			'az',
			'bs',
			'hi',
			'ks',
			'mni',
			'pa',
			'sat',
			'sd',
			'shi',
			'sr',
			'su',
			'uz',
			'vai',
			'yue',
			'zh',
		];
		if (!validString(locale)) {
			return '';
		}
		let localeToTransform: string | string[] = locale;

		if (!localeToTransform.includes('-')) {
			return localeToTransform;
		}

		localeToTransform = localeToTransform.split('-');

		// Make sure to check if the locale has an anti-pattern
		if (localeToTransform.length == 2 && !localesWithAntiPattern.includes(localeToTransform[0])) {
			localeToTransform = `${localeToTransform[0]}-${localeToTransform[1].toLocaleUpperCase()}`;
		} else if (localeToTransform.length == 2 && localesWithAntiPattern.includes(localeToTransform[0])) {
			// Anti-pattern: need to capitalize only the first letter of the second part of the locale
			// Example: xx-Yyyy
			localeToTransform = `${localeToTransform[0]}-${
				localeToTransform[1].charAt(0).toUpperCase() + localeToTransform[1].slice(1).toLowerCase()
			}`;
		} else {
			// If there are 3 parts transform accordingly
			// middle part first letter to uppercase & last part to uppercase
			// Example: xx-Yyyy-ZZ
			localeToTransform = `${localeToTransform[0]}-${
				localeToTransform[1].charAt(0).toUpperCase() + localeToTransform[1].slice(1).toLowerCase()
			}-${localeToTransform[2].toLocaleUpperCase()}`;
		}
		return localeToTransform;
	}

	/**
	 * Parse the locale to a standard language code for angular common locale data
	 * @param locale string
	 * @returns string
	 */
	private _parseLocale(locale: string): string {
		switch (locale) {
			case 'en-fr':
				return 'en-GB';
			case 'fr-gb':
			case 'fr-fr':
				return 'fr';
			case 'th-th':
				return 'th';
			case 'es-nl':
				return 'es';
			case 'nl-nl':
				return 'nl';
			case 'be-be':
				return 'be';
			case 'de-de':
			case 'de-nl':
			case 'de-gb':
				return 'de';
			case 'it-it':
			case 'it-gb':
			case 'it-at':
				return 'it';
			case 'ja-jp':
				return 'ja';
			case 'ko-kr':
				return 'ko';
			case 'en-jp':
			case 'en-kr':
			case 'en-th':
				return 'en-HK';
			case 'zh-hans-hk':
				return 'zh-Hans-HK';
			case 'zh-hans-jp':
			case 'zh-cn':
				return 'zh-Hans';
			case 'zh-hant-hk':
			case 'zh-hk':
				return 'zh-Hant-HK';
			case 'zh-hant-jp':
			case 'zh-tw':
			case 'zh-sg':
				return 'zh-Hant';
			case 'zh-mo':
				return 'zh-Hant-MO';
			default:
				return locale;
		}
	}
}
